import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {computeIsDraftComment} from '../../../../../helpers/models/draftCommentHelpers';
import {AnnotationTypesEnum} from '../../../../../models/api/annotationTypes';
import {CommentModel} from '../../../../../models/api/commentModel';
import {makeGetAnnotation} from '../../../../../store/data/annotations/annotationsSelectors';
import {CellAuthorWrapperProps} from './cellAuthorWrapper';
import {CellCommentLine, CellCommentLineRenderSummaryData, renderSummaryText} from './cellCommentLine';
import {CellSummaryPlaceholder} from './cellSummaryPlaceholder';

/*
 * Props.
 */

export interface CellCommentProps extends CellAuthorWrapperProps {
  comment: CommentModel;
  mentionableAliases?: ReadonlySet<string>;
  isSelected?: boolean;
  isUnread?: boolean;
  hasFollowingComments?: boolean;
  isCompact?: boolean;
}

/*
 * Style.
 */

const StyledAnnotationSummarySpan = styled.span`
  ${ellipsis()};
`;

/*
 * Component.
 */

export const CellComment: FC<CellCommentProps> = (props) => {
  const {comment} = props;
  const annotation = useAppSelector(makeGetAnnotation<void>(() => comment.annotationUid));
  return (
    <CellCommentLine
      isCompact={props.isCompact}
      author={props.author}
      annotation={annotation}
      text={comment.text}
      mentionableAliases={props.mentionableAliases}
      isSelected={props.isSelected}
      isUnread={props.isUnread}
      hasFollowingComments={props.hasFollowingComments}
      attachments={comment.attachments}
      renderSummary={renderSummary}
      shouldDisplayAttachmentIcon={comment.attachments.length > 0}
      // This mirrors the logic in the renderSummary helper below:
      shouldUseEllipsisOverflow={
        Boolean(comment.annotationUid) && !comment.text.trim() && comment.attachments.length === 0
      }
      shouldDisplayDraftStatus={computeIsDraftComment(props.comment)}
    />
  );
};

/*
 * Helpers.
 */

export function renderSummary(data: CellCommentLineRenderSummaryData) {
  const {props, emojiValidationMap} = data;
  const {annotation, text, mentionableAliases, isSelected} = props;
  const attachments = props.attachments ?? [];
  const trimmedText = text.trim();

  // If the comment has text, use it.
  if (trimmedText) {
    return renderSummaryText(trimmedText, emojiValidationMap, isSelected, mentionableAliases);
  }

  // If we have one attachment, use its name.
  if (attachments.length === 1) {
    return attachments[0].name;
  }

  // If multiple attachments, display the plural number of attachments.
  if (attachments.length > 1) {
    return <Trans id="cell-comment-attachments">{attachments.length} attachments</Trans>;
  }

  // If this is just a highlight, render annotation.
  if (annotation?.type === AnnotationTypesEnum.HIGHLIGHT_MESSAGE && annotation.text.trim()) {
    return (
      <>
        <span>&quot;</span>
        <StyledAnnotationSummarySpan>{annotation.text}</StyledAnnotationSummarySpan>
        <span>&quot;</span>
      </>
    );
  }

  // Otherwise, render a placeholder.
  return <CellSummaryPlaceholder />;
}
